import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { getSignedMediaUrl } from '../services/api';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Vizualizator PDF cu DERULARE VERTICALĂ: toate paginile sunt afișate una sub alta și utilizatorul
// le parcurge cu scroll (nu cu butoane înainte/înapoi). Mulți cursanți nu intuiau navigarea
// stânga/dreapta și nu vedeau toate paginile — derularea verticală garantează că trec prin tot.
// Progresul (câte pagini a văzut) e raportat prin onProgress({current,total}) pe baza scroll-ului.
const PdfViewer = ({ url, onProgress }) => {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maxPageReached, setMaxPageReached] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const pageRefs = useRef({});
  const [containerWidth, setContainerWidth] = useState(null);

  // `onProgress` poate fi o funcție nouă la fiecare render (părintele o pasează inline). O ținem în
  // ref ca raportarea progresului să NU depindă de identitatea ei (altfel efectul ar rula la fiecare
  // render → buclă). `lastReportedRef` evită apeluri duplicate pe aceeași pagină.
  const onProgressRef = useRef(onProgress);
  useEffect(() => {
    onProgressRef.current = onProgress;
  });
  const lastReportedRef = useRef(0);

  // URL semnat (autorizat) pentru fișierul protejat. Se obține asincron prin /media/sign.
  const [resolvedUrl, setResolvedUrl] = useState(null);
  useEffect(() => {
    let active = true;
    setResolvedUrl(null);
    getSignedMediaUrl(url).then((u) => {
      if (active) setResolvedUrl(u);
    });
    return () => {
      active = false;
    };
  }, [url]);

  // Reset state when URL changes
  useEffect(() => {
    setNumPages(null);
    setScale(1.0);
    setLoading(true);
    setError(null);
    setMaxPageReached(0);
    setCurrentPage(1);
    pageRefs.current = {};
    lastReportedRef.current = 0;
  }, [url]);

  // Responsive width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Esc iese din fullscreen
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // Raportează cea mai îndepărtată pagină atinsă (monoton crescător). DOAR actualizează state-ul —
  // raportarea către părinte se face în efectul de mai jos, ÎN AFARA render-ului.
  const reportMax = useCallback((page) => {
    setMaxPageReached((prev) => (!page || page <= prev ? prev : page));
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
    setLoading(false);
    setError(null);
    setMaxPageReached(1);
    setCurrentPage(1);
  }, []);

  // Raportează progresul (pagina max atinsă) către părinte ÎN AFARA render-ului (useEffect), doar
  // când valoarea chiar se schimbă. Înlocuiește apelurile `onProgress` din timpul render-ului care
  // declanșau warning-ul „Cannot update a component while rendering a different component".
  useEffect(() => {
    if (!numPages || maxPageReached <= 0) return;
    const current = Math.min(maxPageReached, numPages);
    if (current === lastReportedRef.current) return;
    lastReportedRef.current = current;
    onProgressRef.current?.({ current, total: numPages });
  }, [maxPageReached, numPages]);

  const onDocumentLoadError = useCallback(
    (err) => {
      console.error('PDF load error:', err);
      setError(t('viewers.pdf.loadError'));
      setLoading(false);
    },
    [t]
  );

  // IntersectionObserver: urmărește paginile vizibile pentru progres + indicatorul „pagina curentă".
  useEffect(() => {
    if (!numPages) return;
    const root = containerRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const pn = Number(e.target.dataset.pageNumber);
            if (pn) {
              setCurrentPage(pn);
              reportMax(pn);
            }
          }
        });
      },
      { root, threshold: 0.3 }
    );
    Object.values(pageRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [numPages, reportMax]);

  // Fallback robust: când utilizatorul a derulat aproape de final, marcăm ultima pagină ca atinsă
  // (garantează că materialul poate fi marcat „parcurs" chiar dacă ultima pagină e scurtă).
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el || !numPages) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) reportMax(numPages);
  }, [numPages, reportMax]);

  const zoomIn = () => setScale((s) => Math.min(s + 0.25, 3));
  const zoomOut = () => setScale((s) => Math.max(s - 0.25, 0.5));
  const zoomFit = () => setScale(1.0);

  if (!url) return null;

  const availableWidth = containerWidth ? containerWidth - 32 : 700;
  const pageWidth = Math.min(availableWidth, 900) * scale;
  const allViewed = numPages && maxPageReached >= numPages;

  return (
    <div
      className={
        isFullscreen
          ? 'fixed inset-0 z-50 flex flex-col bg-slate-50 dark:bg-slate-900'
          : 'flex flex-col bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden'
      }
      style={isFullscreen ? { height: '100vh' } : { height: 'calc(65vh)' }}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
        {/* Indicator pagină curentă (din scroll) */}
        <span className="text-sm text-slate-700 dark:text-slate-300 font-medium tabular-nums min-w-[70px] text-center select-none">
          {currentPage} / {numPages || '...'}
        </span>

        {/* Zoom */}
        <div className="flex items-center gap-0.5">
          <button
            onClick={zoomOut}
            disabled={scale <= 0.5}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <svg
              className="w-4 h-4 text-slate-500 dark:text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
            </svg>
          </button>
          <button
            onClick={zoomFit}
            className="px-2 py-0.5 text-xs font-medium text-slate-500 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors tabular-nums"
          >
            {Math.round(scale * 100)}%
          </button>
          <button
            onClick={zoomIn}
            disabled={scale >= 3}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <svg
              className="w-4 h-4 text-slate-500 dark:text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        {/* Progress + Fullscreen */}
        <div className="flex items-center gap-2">
          {numPages && numPages > 1 && (
            <div className="flex items-center gap-1.5">
              <div className="w-16 bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${allViewed ? 'bg-green-500' : 'bg-blue-500'}`}
                  style={{ width: `${numPages ? (maxPageReached / numPages) * 100 : 0}%` }}
                />
              </div>
              {allViewed ? (
                <svg
                  className="w-4 h-4 text-green-500 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="text-xs text-slate-400 dark:text-slate-400 tabular-nums flex-shrink-0">
                  {maxPageReached}/{numPages}
                </span>
              )}
            </div>
          )}
          <button
            onClick={() => setIsFullscreen((f) => !f)}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title={isFullscreen ? t('viewers.exitFullscreen') : t('viewers.fullscreen')}
          >
            {isFullscreen ? (
              <svg
                className="w-4 h-4 text-slate-600 dark:text-slate-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4 text-slate-600 dark:text-slate-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* PDF content — toate paginile stivuite vertical, parcurse prin scroll */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-800"
        style={{ minHeight: 0 }}
      >
        {error ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500 dark:text-slate-400">
            <svg
              className="w-12 h-12 mb-3 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
            <p className="text-sm font-medium">{error}</p>
            <a
              href={resolvedUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 text-sm text-blue-600 hover:underline"
            >
              {t('viewers.pdf.openFile')}
            </a>
          </div>
        ) : !resolvedUrl ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
          </div>
        ) : (
          <Document
            key={url}
            file={resolvedUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
              </div>
            }
          >
            <div className="py-3 px-4 flex flex-col items-center gap-4">
              {Array.from({ length: numPages || 0 }, (_, i) => i + 1).map((pn) => (
                <div
                  key={pn}
                  data-page-number={pn}
                  ref={(el) => {
                    if (el) pageRefs.current[pn] = el;
                  }}
                  className="shadow-lg rounded bg-white"
                  style={{ width: 'fit-content' }}
                >
                  <Page
                    pageNumber={pn}
                    width={pageWidth}
                    loading={
                      <div
                        className="flex items-center justify-center"
                        style={{ width: pageWidth, height: pageWidth * 1.4 }}
                      >
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
                      </div>
                    }
                  />
                </div>
              ))}
            </div>
          </Document>
        )}
      </div>

      {/* Bottom hint */}
      {numPages > 1 && (
        <div className="px-3 py-1.5 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-800 flex-shrink-0">
          <p className="text-xs text-slate-400 dark:text-slate-400 text-center">
            {t('viewers.pdf.hint')}
          </p>
        </div>
      )}
    </div>
  );
};

export default PdfViewer;
